// Capa de acceso a la API de Comando (DRF).

import axios from 'axios';

// La sesión viaja en cookie y el CSRF en la cabecera: axios lee `csrftoken` y
// lo manda en X-CSRFToken, que es lo que espera SessionAuthentication.
const api = axios.create({
  baseURL: '/api/',
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

const RUTA_LOGIN = '/login';

// Sesión vencida a mitad de camino: se vuelve al login guardando dónde
// estábamos. /sesion/ se salta: ahí un 401 es "clave mala" y lo muestra el form.
api.interceptors.response.use(
  (respuesta) => respuesta,
  (error) => {
    const status = error.response && error.response.status;
    const url = (error.config && error.config.url) || '';
    const enLogin = window.location.pathname.startsWith(RUTA_LOGIN);
    if ((status === 401 || status === 403) && !url.startsWith('sesion') && !enLogin) {
      const destino = window.location.pathname + window.location.search;
      window.location.assign(`${RUTA_LOGIN}?next=${encodeURIComponent(destino)}`);
    }
    return Promise.reject(error);
  },
);

// --- Sesión -----------------------------------------------------------------

// Abre la sesión. Devuelve el usuario y a dónde ir después.
export async function iniciarSesion(usuario, clave, next) {
  const { data } = await api.post('sesion/', { usuario, clave, next });
  return data;
}

export async function cerrarSesion() {
  await api.delete('sesion/');
}

// --- Mapa y consultas -------------------------------------------------------

export async function obtenerConfig() {
  const { data } = await api.get('config/');
  return data;
}

// Sube el audio grabado y devuelve la consulta recién creada (con su id).
export async function enviarAudio(blob, { lat, lng, segundos, tipoTerreno } = {}, onProgress) {
  const form = new FormData();
  // La extensión viaja hasta Bedrock: define la key en S3, y de ahí sale el
  // formato que se declara en la transcripción.
  const tipo = blob.type || '';
  let extension = 'wav';
  if (tipo.includes('mp3') || tipo.includes('mpeg')) extension = 'mp3';
  else if (tipo.includes('mp4')) extension = 'mp4';
  else if (tipo.includes('webm')) extension = 'webm';
  form.append('audio', blob, `consulta.${extension}`);
  if (lat != null) form.append('lat', lat);
  if (lng != null) form.append('lng', lng);
  if (segundos != null) form.append('segundos', segundos);
  if (tipoTerreno) form.append('tipo', tipoTerreno);

  const { data } = await api.post('consultas-voz/', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (evento) => {
      if (onProgress && evento.total) {
        onProgress(Math.round((evento.loaded * 100) / evento.total));
      }
    },
  });
  return data;
}

export async function estadoConsulta(id) {
  const { data } = await api.get(`consultas-voz/${id}/`);
  return data;
}

// Repregunta hasta que el pipeline termina (o falla). Intervalo corto: dura
// pocos segundos, y con 1200 ms el resultado esperaba ya estando listo.
export async function esperarResultado(id, { onAvance, intervalo = 400, timeout = 240000 } = {}) {
  const limite = Date.now() + timeout;

  for (;;) {
    const consulta = await estadoConsulta(id);
    if (onAvance) onAvance(consulta);

    if (consulta.status === 'listo') return consulta;
    if (consulta.status === 'error') {
      throw new Error(consulta.error || 'La consulta terminó con error.');
    }
    if (Date.now() > limite) {
      throw new Error('La consulta se demoró más de lo esperado.');
    }

    await new Promise((resolve) => setTimeout(resolve, intervalo));
  }
}


// --- Tablero de negocio (Comando 5) ------------------------------------------

export async function negocioCategorias() {
  const { data } = await api.get('negocio/categorias/');
  return data;
}

export async function negocioTablero(categoria, { fase, q } = {}) {
  const { data } = await api.get('negocio/tablero/', { params: { categoria, fase, q } });
  return data;
}

// El HOME de la categoría: unidades con alertas, acciones, documentos y urgencia.
export async function negocioHome(categoria) {
  const { data } = await api.get('negocio/home/', { params: { categoria } });
  return data.unidades;
}

export async function negocioResolverAlerta(unidad, alerta) {
  const { data } = await api.post(`negocio/unidades/${unidad}/alertas/${alerta}/resolver/`);
  return data;
}

export async function negocioClientes(q) {
  const { data } = await api.get('negocio/clientes/', { params: { q } });
  return data.clientes;
}

export async function negocioCrearCliente(cliente) {
  const { data } = await api.post('negocio/clientes/', cliente);
  return data;
}

// La unidad nace siempre en la etapa inicial de la industria de la categoría.
export async function negocioCrearUnidad(categoria, terreno, cliente) {
  const { data } = await api.post('negocio/unidades/', { categoria, terreno, cliente });
  return data;
}

// Busca en el catálogo de terrenos por nombre, comuna o rol.
export async function negocioTerrenos(q) {
  const { data } = await api.get('negocio/terrenos/', { params: { q } });
  return data.terrenos;
}

export async function negocioUnidad(id) {
  const { data } = await api.get(`negocio/unidades/${id}/`);
  return data;
}

export async function negocioAvanzar(id) {
  const { data } = await api.post(`negocio/unidades/${id}/avanzar/`);
  return data;
}

export async function negocioGestores() {
  const { data } = await api.get('negocio/gestores/');
  return data.gestores;
}

export async function negocioComisiones(id) {
  const { data } = await api.get(`negocio/unidades/${id}/comisiones/`);
  return data;
}

// Asigna el director y, si viene alguna parte o nota, agrega la fila de comisión.
export async function negocioGuardarComision(id, comision) {
  const { data } = await api.post(`negocio/unidades/${id}/comisiones/`, comision);
  return data;
}

// Con `tipo`, el usuario deja además ese hito o acción en la bitácora.
export async function negocioMoverFase(id, fase, { tipo, nota } = {}) {
  const { data } = await api.post(`negocio/unidades/${id}/fase/`, { fase, tipo, nota });
  return data;
}

export async function negocioAnotar(id, { tipo, nota, fechaTope, accion }) {
  const { data } = await api.post(`negocio/unidades/${id}/bitacora/`, {
    tipo, nota, fecha_tope: fechaTope || null, accion,
  });
  return data;
}

export async function negocioBorrarRegistro(id) {
  const { data } = await api.delete(`negocio/bitacora/${id}/`);
  return data;
}

// Sube el archivo de un documento de la industria; si la etapa no llega, lo anticipa.
export async function negocioSubirDocumento(unidad, documento, archivo) {
  const form = new FormData();
  form.append('archivo', archivo);
  const { data } = await api.post(`negocio/unidades/${unidad}/documentos/${documento}/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function negocioCumplirAccion(unidad, accion) {
  const { data } = await api.post(`negocio/unidades/${unidad}/acciones/${accion}/`);
  return data;
}

// Edita columnas de la Planilla Master (fecha_solicitud, en_tramite, fisico, carpeta, referencia).
export async function negocioEditarPlanilla(unidad, documento, campos) {
  const { data } = await api.post(`negocio/unidades/${unidad}/planilla/${documento}/`, campos);
  return data;
}

// El catálogo de documentos de la industria de la categoría, sin unidad.
export async function negocioDocumentosIndustria(categoria) {
  const { data } = await api.get('negocio/documentos/', { params: { categoria } });
  return data.documentos;
}

// `item` es { documento: id } o { accion: id }; `vence` va como AAAA-MM-DD.
export async function negocioCambiarPlazo(unidad, item, vence, razon) {
  const { data } = await api.post(`negocio/unidades/${unidad}/plazo/`, { ...item, vence, razon });
  return data;
}

// Lo del modal de envío de INFO y el informe de envíos de la unidad.
export async function negocioEnvios(unidad) {
  const { data } = await api.get(`negocio/unidades/${unidad}/envios/`);
  return data;
}

// `envio` es { nivel, asunto, mensaje, contactos: [ids], adjuntos: [ids] }.
export async function negocioEnviar(unidad, envio) {
  const { data } = await api.post(`negocio/unidades/${unidad}/envios/`, envio);
  return data;
}

export async function negocioReenviar(destinatario) {
  const { data } = await api.post(`negocio/envios/${destinatario}/reenviar/`);
  return data;
}

// El informe de envíos de toda la categoría.
export async function negocioEnviosCategoria(categoria) {
  const { data } = await api.get('negocio/envios/', { params: { categoria } });
  return data.destinatarios;
}

// El 400 de la API trae el porqué en `detail`; se muestra tal cual.
export function mensajeError(error) {
  const detalle = error.response && error.response.data && error.response.data.detail;
  return detalle || error.message || 'Error desconocido.';
}
